/**
 * ============================================================================
 * InviteLandingPage.tsx - Public Landing Page for Invite Links
 * ============================================================================
 * 
 * PRODUCTION-READY landing page that handles invite link validation.
 *  ENTERPRISE: Mobile-optimized with responsive design

 * 
 * URL Format: /join/:inviteToken
 * 
 * Flow:
 * 1. User clicks invite link → lands on this page
 * 2. Validates the invite token with backend
 * 3. Shows table info if valid
 * 4. If authenticated → redirect to game with token
 * 5. If not authenticated → store token → redirect to login
 * 6. After login → LoginPage handles redirect to game
 * 7. New users go through onboarding first, then to game
 * 
 * Handles:
 * - Valid tokens → show table info and join button
 * - Expired tokens → show error with option to go home
 * - Invalid tokens → show error
 * - Full tables → show full message
 * - Network errors → show retry option
 * 
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Badge,
  Container,
} from '@chakra-ui/react';
import { auth } from '../services/firebase';
import { 
  validateInviteToken, 
  storePendingInvite,
  TokenValidationResult 
} from '../services/inviteService';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import { POKER_COLORS } from '../theme/designSystem';

const InviteLandingPage: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenData, setTokenData] = useState<TokenValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [joining, setJoining] = useState(false);

  // ============================================================================
  // CHECK AUTH STATE
  // ============================================================================
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ============================================================================
  // VALIDATE TOKEN ON MOUNT
  // ============================================================================
  useEffect(() => {
    const validateToken = async () => {
      if (!inviteToken) {
        setError('Invalid invite link');
        setValidating(false);
        return;
      }

      // Basic validation
      if (inviteToken.length < 32) {
        setError('Invalid invite link format');
        setValidating(false);
        return;
      }

      try {
        console.log('🔗 [INVITE] Validating invite token...');
        const result = await validateInviteToken(inviteToken);

        if (result.valid) {
          setTokenData(result);
          console.log('✅ [INVITE] Token valid for table:', result.tableName);
        } else {
          setError(result.error || 'Invalid or expired invite link');
          console.warn('⚠️ [INVITE] Token validation failed:', result.error);
        }
      } catch (err) {
        console.error('❌ [INVITE] Token validation error:', err);
        setError('Could not validate invite link. Please check your connection and try again.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [inviteToken]);

  // ============================================================================
  // HANDLE JOIN TABLE
  // ============================================================================
  const handleJoinTable = async () => {
    if (!inviteToken || !tokenData?.tableId) return;

    setJoining(true);

    try {
      if (isAuthenticated) {
        // Already logged in - store token and go to game
        storePendingInvite(inviteToken, tokenData.tableId);
        console.log('🎮 [INVITE] User authenticated, redirecting to game');
        navigate(`/game/${tokenData.tableId}?invite=${inviteToken}`);
      } else {
        // Not logged in - save token and redirect to login
        storePendingInvite(inviteToken, tokenData.tableId);
        console.log('🔐 [INVITE] User not authenticated, redirecting to login');
        // Carry the host's referral code into signup so the friend and the
        // host each get the 5 PSS Coin referral bonus.
        const ref = tokenData.inviterReferralCode;
        navigate(`/login?redirect=invite${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`);
      }
    } catch (err) {
      console.error('Error handling join:', err);
      setError('Failed to process invite. Please try again.');
      setJoining(false);
    }
  };

  // ============================================================================
  // HANDLE RETRY
  // ============================================================================
  const handleRetry = () => {
    setError(null);
    setValidating(true);
    // Re-trigger validation
    window.location.reload();
  };

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================
  if (loading || validating) {
    return (
      <Box
        minH="100dvh"
        bg={POKER_COLORS.darkBase}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={{ base: 3, md: 4 }}
      >
        <VStack spacing={6}>
          <Spinner 
            size="xl" 
            color={POKER_COLORS.cyan} 
            thickness="4px"
            speed="0.8s"
          />
          <Text color="gray.400" fontSize={{ base: 'md', md: 'lg' }}>
            Validating invite link...
          </Text>
        </VStack>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: ERROR STATE
  // ============================================================================
  if (error || !tokenData?.valid) {
    return (
      <Box
        minH="100dvh"
        bg={POKER_COLORS.darkBase}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={{ base: 3, md: 4 }}
      >
        <Container maxW="md" px={{ base: 2, md: 4 }}>
          <PokerCard glow={false}>
            <VStack spacing={{ base: 4, md: 6 }} textAlign="center" py={{ base: 2, md: 4 }}>
              <Text fontSize={{ base: '48px', md: '60px' }}></Text>

              
              <Heading size={{ base: 'md', md: 'lg' }} color="white">
                Invalid Invite Link
              </Heading>
              
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                {error || 'This invite link is invalid or has expired.'}
              </Text>
              
              <VStack spacing={3} w="100%" pt={{ base: 2, md: 4 }}>
                {error?.includes('connection') && (
                  <PokerButton
                    variant="primary"
                    w="100%"
                    h={{ base: '52px', md: '48px' }}
                    onClick={handleRetry}
                    _active={{ transform: 'scale(0.98)' }}
                  >
                     Try Again

                  </PokerButton>
                )}
                
                <PokerButton
                  variant="secondary"
                  w="100%"
                  h={{ base: '52px', md: '48px' }}
                  onClick={() => navigate('/')}
                  _active={{ transform: 'scale(0.98)' }}
                >
                   Go to Home

                </PokerButton>
                
                <PokerButton
                  variant="ghost"
                  w="100%"
                  h={{ base: '52px', md: '48px' }}
                  onClick={() => navigate('/tables')}
                  _active={{ transform: 'scale(0.98)' }}
                >
                   Browse Tables

                </PokerButton>
              </VStack>
            </VStack>
          </PokerCard>
        </Container>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: TABLE FULL STATE
  // ============================================================================
  if (tokenData.isFull) {
    return (
      <Box
        minH="100dvh"
        bg={POKER_COLORS.darkBase}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={{ base: 3, md: 4 }}
      >
        <Container maxW="md" px={{ base: 2, md: 4 }}>
          <PokerCard glow={false}>
            <VStack spacing={{ base: 4, md: 6 }} textAlign="center" py={{ base: 2, md: 4 }}>
              <Text fontSize={{ base: '48px', md: '60px' }}></Text>

              
              <Heading size={{ base: 'md', md: 'lg' }} color="white">
                Table is Full
              </Heading>
              
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                <Text as="span" color={POKER_COLORS.cyan} fontWeight="bold" maxW="200px" isTruncated>
                  {tokenData.tableName}
                </Text>
                {' '}is currently at capacity ({tokenData.currentPlayers}/{tokenData.maxSeats} players).
              </Text>
              
              <Text color="gray.500" fontSize={{ base: 'xs', md: 'sm' }}>
                Try again later or ask the host to make room.
              </Text>
              
              <VStack spacing={3} w="100%" pt={{ base: 2, md: 4 }}>
                <PokerButton
                  variant="primary"
                  w="100%"
                  h={{ base: '52px', md: '48px' }}
                  onClick={() => navigate('/tables')}
                  _active={{ transform: 'scale(0.98)' }}
                >
                   Browse Other Tables

                </PokerButton>
                
                <PokerButton
                  variant="ghost"
                  w="100%"
                  h={{ base: '52px', md: '48px' }}
                  onClick={() => navigate('/')}
                  _active={{ transform: 'scale(0.98)' }}
                >
                   Go to Home

                </PokerButton>
              </VStack>
            </VStack>
          </PokerCard>
        </Container>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: VALID INVITE - SHOW TABLE INFO
  // ============================================================================
  return (
    <Box
      minH="100dvh"
      bg={POKER_COLORS.darkBase}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={{ base: 3, md: 4 }}
    >
      <Container maxW="md" px={{ base: 2, md: 4 }}>
        <PokerCard glow={true}>
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center" py={{ base: 1, md: 2 }}>
            {/* Header */}
            <Text fontSize={{ base: '48px', md: '60px' }}></Text>

            
            <VStack spacing={1}>
              <Heading size={{ base: 'md', md: 'lg' }} color="white">
                You're Invited!
              </Heading>
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                Join the action at the poker table
              </Text>
            </VStack>

            {/* Table Info Card -  MOBILE: Compact layout */}

            <Box
              w="100%"
              bg={POKER_COLORS.darkBase}
              p={{ base: 3, md: 5 }}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.700"
            >
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                {/* Table Name */}
                <HStack justify="space-between">
                  <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Table</Text>
                  <Text 
                    color="white" 
                    fontWeight="bold" 
                    fontSize={{ base: 'md', md: 'lg' }}
                    maxW="200px"
                    isTruncated
                  >
                    {tokenData.tableName}
                  </Text>
                </HStack>

                {/* Stakes */}
                <HStack justify="space-between">
                  <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Stakes</Text>
                  <Text color={POKER_COLORS.cyan} fontWeight="semibold" fontSize={{ base: 'sm', md: 'md' }}>
                    {tokenData.smallBlind}/{tokenData.bigBlind}
                  </Text>
                </HStack>

                {/* Buy-in Range */}
                <HStack justify="space-between">
                  <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Buy-in</Text>
                  <Text color={POKER_COLORS.gold} fontWeight="semibold" fontSize={{ base: 'sm', md: 'md' }}>
                    {tokenData.minBuyIn} - {tokenData.maxBuyIn}
                  </Text>
                </HStack>

                {/*  MOBILE: Combined row for currency & players */}

                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <HStack>
                    <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Currency:</Text>
                    <Badge
                      colorScheme={tokenData.currency === 'pkcash' ? 'green' : 'cyan'}
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                      {tokenData.currency === 'pkcash' ? ' PSS Coins' : ' PlayCoins'}

                    </Badge>
                  </HStack>
                  <HStack>
                    <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Players:</Text>
                    <Text color="white" fontWeight="semibold" fontSize={{ base: 'sm', md: 'md' }}>
                      {tokenData.currentPlayers}/{tokenData.maxSeats}
                    </Text>
                    {tokenData.currentPlayers && tokenData.currentPlayers > 0 && (
                      <Badge colorScheme="green" variant="subtle" fontSize="xs">
                        Active
                      </Badge>
                    )}
                  </HStack>
                </HStack>

                {/* Private Badge */}
                {tokenData.isPrivate && (
                  <HStack justify="center" pt={1}>
                    <Badge 
                      colorScheme="purple" 
                      px={{ base: 2, md: 4 }}
                      py={{ base: 1, md: 2 }}
                      borderRadius="full"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                       Private Table - Invite Grants Access

                    </Badge>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Auth Status Message */}
            {!isAuthenticated && (
              <Box
                w="100%"
                bg="blue.900"
                bgOpacity={0.3}
                p={{ base: 2, md: 3 }}
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.700"
              >
                <Text color="blue.200" fontSize={{ base: 'xs', md: 'sm' }} textAlign="center">
                   You'll need to sign in or create an account to join

                </Text>
                {tokenData?.inviterReferralCode && (
                  <Text color="yellow.300" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold" textAlign="center" mt={1}>
                    New here? Sign up from this invite and you{tokenData.inviterName ? ` and ${tokenData.inviterName}` : ''} each get 5 free PSS Coins.
                  </Text>
                )}
              </Box>
            )}

            {/* Join Button -  MOBILE: Larger touch target */}

            <PokerButton
              variant="primary"
              w="100%"
              size="lg"
              h={{ base: '56px', md: '52px' }}
              onClick={handleJoinTable}
              isLoading={joining}
              loadingText="Joining..."
              leftIcon={<Text></Text>}

              _active={{ transform: 'scale(0.98)' }}
            >
              {isAuthenticated ? 'Join Table' : 'Sign In & Join'}
            </PokerButton>

            {/* Secondary Actions -  MOBILE: Stacked on small screens */}

            <VStack spacing={2} w="100%" display={{ base: 'flex', md: 'none' }}>
              <PokerButton
                variant="ghost"
                w="100%"
                h="48px"
                onClick={() => navigate('/')}
                _active={{ transform: 'scale(0.98)' }}
              >
                Maybe Later
              </PokerButton>
              
              {!isAuthenticated && (
                <PokerButton
                  variant="ghost"
                  w="100%"
                  h="48px"
                  onClick={() => navigate('/login')}
                  _active={{ transform: 'scale(0.98)' }}
                >
                  Just Sign In
                </PokerButton>
              )}
            </VStack>

            {/* Desktop: Side by side */}
            <HStack spacing={4} w="100%" display={{ base: 'none', md: 'flex' }}>
              <PokerButton
                variant="ghost"
                flex={1}
                onClick={() => navigate('/')}
              >
                Maybe Later
              </PokerButton>
              
              {!isAuthenticated && (
                <PokerButton
                  variant="ghost"
                  flex={1}
                  onClick={() => navigate('/login')}
                >
                  Just Sign In
                </PokerButton>
              )}
            </HStack>
          </VStack>
        </PokerCard>
      </Container>
    </Box>
  );
};

export default InviteLandingPage;